import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useIsWaitlist } from "@/context/ConfigContext";
import { toast } from "sonner";
import { Clock, Video, MapPin, Shield } from "lucide-react";
import WaitlistForm from "@/components/WaitlistForm";

export default function Booking() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isWaitlist = useIsWaitlist();
  const [sessionType, setSessionType] = useState("intro_call");
  const [modality] = useState("virtual"); // in-person disabled until office opens
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [notes, setNotes] = useState("");
  const [services, setServices] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);
  useEffect(() => {
    setSlot(null);
    api.get(`/availability?date_str=${selectedDate}`).then((r) => setSlots(r.data.slots));
  }, [selectedDate]);

  const nextDays = Array.from({ length: 14 }).map((_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i); return d;
  });

  const localizedSessionName = (key, s) => {
    const map = {
      intro_call: t({ en: "Complimentary 15-Min Consultation Call", fr: "Consultation vidéo gratuite (15 min)" }),
      followup_50: t({ en: "Counselling Session (50 min)", fr: "Séance de counseling (50 min)" }),
    };
    return map[key] || s.name;
  };

  const submit = async () => {
    if (!user) { nav(`/login?redirect=/book`); return; }
    if (!slot) { toast.error(t({ en: "Please pick a time slot.", fr: "Veuillez choisir un créneau horaire." })); return; }
    if (!policyAgreed) { toast.error(t({ en: "Please agree to the 24-hour cancellation policy.", fr: "Veuillez accepter la politique d'annulation de 24 heures." })); return; }
    setSubmitting(true);
    try {
      const b = await api.post("/bookings", { session_type: sessionType, start_iso: slot, modality, notes, policy_agreed: true });
      const booking = b.data;
      if (booking.amount > 0) {
        const co = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
      } else {
        toast.success(t({ en: "Your consultation call is confirmed. Check your email.", fr: "Votre consultation est confirmée. Vérifiez votre courriel." }));
        nav("/portal");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t({ en: "Booking failed.", fr: "Échec de la réservation." }));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16 lg:py-24" data-testid="booking-page">
      {isWaitlist && (
        <div className="mb-10 p-6 rounded-xl" style={{ background: "var(--surface-muted)", border: "1px solid var(--ochre)" }} data-testid="waitlist-banner">
          <div className="font-serif text-2xl mb-2" style={{ color: "var(--ink)" }}>{t({ en: "Bookings are currently closed.", fr: "Les réservations sont actuellement fermées." })}</div>
          <p className="text-sm mb-4" style={{ color: "var(--ink-2)" }}>{t({ en: "The practice is preparing to reopen. Join the waitlist below and I'll email you personally when the calendar opens.", fr: "Le cabinet se prépare à rouvrir. Rejoignez la liste d'attente ci-dessous et je vous écrirai dès l'ouverture du calendrier." })}</p>
          <WaitlistForm interestedIn="counselling" tone="light" />
        </div>
      )}
      <div className="overline mb-4">{t({ en: "Book a session", fr: "Prendre rendez-vous" })}</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 tracking-tight" style={{ color: "var(--ink)" }}>{t({ en: "Choose a time that fits.", fr: "Choisissez un moment qui vous convient." })}</h1>
      <p className="text-lg max-w-2xl mb-12" style={{ color: "var(--ink-2)" }}>{t({ en: "All times shown in Eastern Time. Sessions are limited to once per week per client.", fr: "Toutes les heures sont indiquées en heure de l'Est. Les séances sont limitées à une par semaine par client·e." })}</p>

      {!user && (
        <div className="p-4 mb-8 rounded-lg text-sm flex items-center justify-between flex-wrap gap-3" style={{ background: "var(--sage)", color: "var(--brand)" }}>
          <span>{t({ en: "You'll need to sign in or create an account before confirming your booking.", fr: "Vous devrez vous connecter ou créer un compte avant de confirmer votre réservation." })}</span>
          <div className="flex gap-2">
            <Link to="/login?redirect=/book" className="btn-ghost text-sm py-2 px-4">{t({ en: "Sign in", fr: "Connexion" })}</Link>
            <Link to="/register?redirect=/book" className="btn-primary text-sm py-2 px-4">{t({ en: "Create account", fr: "Créer un compte" })}</Link>
          </div>
        </div>
      )}

      <section className="mb-10">
        <div className="overline mb-3">01 · {t({ en: "Session type", fr: "Type de séance" })}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          {Object.entries(services).map(([key, s]) => (
            <button key={key} onClick={() => setSessionType(key)} className={`p-5 rounded-xl border text-left transition ${sessionType === key ? "border-[var(--terracotta)] bg-[var(--surface)]" : "border-[var(--border-medium)] bg-[var(--surface)]"}`} data-testid={`session-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} style={{ color: "var(--brand)" }} />
                <span className="overline">{s.duration} min</span>
              </div>
              <div className="font-serif text-lg mb-1" style={{ color: "var(--ink)" }}>{localizedSessionName(key, s)}</div>
              <div className="font-serif text-2xl" style={{ color: "var(--terracotta)" }}>{s.amount === 0 ? t({ en: "Free", fr: "Gratuit" }) : `$${s.amount} ${s.currency.toUpperCase()}`}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <div className="overline mb-3">02 · {t({ en: "Modality", fr: "Modalité" })}</div>
        <div className="flex flex-wrap gap-3 items-center">
          <button className="px-5 py-3 rounded-full text-sm flex items-center gap-2 border bg-[var(--brand)] text-[#FBF9F5] border-[var(--brand)]" data-testid="modality-virtual"><Video size={14} /> {t({ en: "Virtual", fr: "Virtuel" })}</button>
          <button disabled className="px-5 py-3 rounded-full text-sm flex items-center gap-2 border opacity-50 cursor-not-allowed bg-[var(--surface)] border-[var(--border-medium)]" data-testid="modality-in-person"><MapPin size={14} /> {t({ en: "In-person (Montreal)", fr: "En présentiel (Montréal)" })} <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ochre)" }}>{t({ en: "Coming soon", fr: "Bientôt" })}</span></button>
        </div>
        <p className="mt-3 text-xs" style={{ color: "var(--ink-3)" }}>{t({ en: "My Montreal office opens soon. Until then, all sessions are virtual. Ontario clients are seen virtually only.", fr: "Mon bureau de Montréal ouvre bientôt. D'ici là, toutes les séances sont virtuelles. Les client·es de l'Ontario sont vu·es en virtuel seulement." })}</p>
      </section>

      <section className="mb-10">
        <div className="overline mb-3">03 · {t({ en: "Date", fr: "Date" })}</div>
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="date-strip">
          {nextDays.map((d) => {
            const iso = d.toISOString().slice(0, 10);
            const active = iso === selectedDate;
            const locale = lang === "fr" ? "fr-CA" : "en-US";
            return (
              <button key={iso} onClick={() => setSelectedDate(iso)} className={`shrink-0 flex flex-col items-center p-3 rounded-xl border min-w-[68px] ${active ? "bg-[var(--brand)] text-[#FBF9F5] border-[var(--brand)]" : "bg-[var(--surface)] border-[var(--border-medium)] text-[var(--ink-2)]"}`} data-testid={`date-${iso}`}>
                <span className="text-[10px] uppercase tracking-widest">{d.toLocaleDateString(locale, { weekday: "short" })}</span>
                <span className="font-serif text-2xl mt-1">{d.getDate()}</span>
                <span className="text-[10px] uppercase">{d.toLocaleDateString(locale, { month: "short" })}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mb-10">
        <div className="overline mb-3">04 · {t({ en: "Time slot (EST)", fr: "Créneau horaire (HE)" })}</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="slot-grid">
          {slots.length === 0 && <div className="col-span-full text-sm" style={{ color: "var(--ink-3)" }}>{t({ en: "No availability on this day.", fr: "Aucune disponibilité ce jour-là." })}</div>}
          {slots.map((s) => {
            const localTime = new Date(s.start_utc).toLocaleTimeString(lang === "fr" ? "fr-CA" : "en-CA", { timeZone: "America/Toronto", hour: "2-digit", minute: "2-digit", hour12: lang !== "fr" });
            const active = slot === s.start_utc;
            return (
              <button key={s.start_utc} disabled={s.taken} onClick={() => setSlot(s.start_utc)} className={`p-3 rounded-lg text-sm border transition ${s.taken ? "opacity-40 line-through cursor-not-allowed" : ""} ${active ? "bg-[var(--terracotta)] text-white border-[var(--terracotta)]" : "bg-[var(--surface)] border-[var(--border-medium)] hover:border-[var(--terracotta)]"}`} data-testid={`slot-${s.start_utc}`}>
                {localTime}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mb-10">
        <div className="overline mb-3">05 · {t({ en: "Anything you'd like me to know (optional)", fr: "Ce que vous aimeriez que je sache (facultatif)" })}</div>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full p-3 rounded-lg border" style={{ borderColor: "var(--border-medium)", background: "var(--surface)" }} data-testid="booking-notes" />
      </section>

      <label className="flex items-start gap-3 p-4 rounded-lg cursor-pointer mb-8" style={{ background: "var(--bg-secondary)" }}>
        <input type="checkbox" checked={policyAgreed} onChange={(e) => setPolicyAgreed(e.target.checked)} className="mt-1" data-testid="policy-checkbox" />
        <span className="text-sm flex-1" style={{ color: "var(--ink-2)" }}>
          <Shield size={14} className="inline mr-1" style={{ color: "var(--terracotta)" }} />
          {t({ en: "I understand that missed or late-cancelled sessions are charged in full unless I provide at least 24 hours notice.", fr: "Je comprends que les séances manquées ou annulées tardivement sont facturées en entier à moins d'un préavis d'au moins 24 heures." })}
        </span>
      </label>

      <button onClick={submit} disabled={submitting || !slot} className="btn-accent w-full justify-center text-base py-4" data-testid="btn-confirm-booking">
        {submitting ? t({ en: "Processing…", fr: "Traitement…" }) : sessionType === "intro_call" ? t({ en: "Confirm consultation call", fr: "Confirmer la consultation" }) : t({ en: "Continue to payment", fr: "Continuer vers le paiement" })}
      </button>
    </div>
  );
}
